import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function sessionId() {
  let id = localStorage.getItem("agentica_chat_session");
  if (!id) {
    id = `sess-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    localStorage.setItem("agentica_chat_session", id);
  }
  return id;
}

const URL_RE = /(https?:\/\/[^\s<>()*]+[^\s<>()*.,;:!?'"])/g;
const IS_URL = /^https?:\/\//;
const BOLD_RE = /(\*\*[^*]+\*\*)/g;

// Turn plain URLs in assistant replies into clickable links
function linkify(text) {
  return text.split(URL_RE).map((part, i) =>
    IS_URL.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer"
        className="text-[#9d6bff] underline underline-offset-2 hover:text-white transition-colors break-all"
        data-testid="chat-link">
        {part.replace(/^https?:\/\//, "").replace(/\/$/, "")}
      </a>
    ) : part
  );
}

// Minimal markdown: **bold** + clickable links
function renderAssistant(text) {
  return text.split(BOLD_RE).map((chunk, i) =>
    chunk.startsWith("**") && chunk.endsWith("**")
      ? <strong key={i} className="text-white font-500">{linkify(chunk.slice(2, -2))}</strong>
      : <span key={i}>{linkify(chunk)}</span>
  );
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I'm Maaya Assistant — Agentica's AI concierge. Ask me about our consulting, engineering, or our products: Docu Flow, Video Analysis, and Clinical Flow." },
  ]);
  const scrollRef = useRef(null);
  const session = useRef(sessionId());

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session.current, message: text }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I'm having trouble connecting. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.4, type: "spring", stiffness: 200, damping: 18 }}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[60] h-14 w-14 rounded-full bg-white text-void flex items-center justify-center shadow-2xl hover:bg-gradient-to-r hover:from-[#4f6bff] hover:to-[#9d6bff] hover:text-white transition-[background-color,color] duration-300"
        data-testid="chatbot-toggle"
        aria-label="Open AI assistant"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="h-6 w-6" />
            </motion.span>
          ) : (
            <motion.span key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageSquare className="h-6 w-6" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[400px] h-[540px] max-h-[75vh] rounded-3xl border border-white/10 bg-black/70 backdrop-blur-xl flex flex-col overflow-hidden shadow-2xl"
            data-testid="chatbot-panel"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
              <span className="h-9 w-9 rounded-full bg-[#8b7cff]/15 border border-[#8b7cff]/30 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-[#8b7cff]" />
              </span>
              <div>
                <p className="text-white font-500 text-sm">Maaya Assistant</p>
                <p className="text-white/40 text-xs">Agentica AI concierge</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4" data-testid="chatbot-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-white text-void rounded-br-sm"
                        : "bg-white/5 border border-white/10 text-white/85 rounded-bl-sm"
                    }`}
                  >
                    {m.content
                      ? (m.role === "assistant" ? renderAssistant(m.content) : m.content)
                      : (busy && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-white/10">
              <div className="flex items-center gap-2 rounded-full border border-white/10 bg-black/40 pl-4 pr-1.5 py-1.5">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask about our services…"
                  className="flex-1 bg-transparent text-sm text-white placeholder:text-white/30 outline-none"
                  data-testid="chatbot-input"
                />
                <button
                  onClick={send}
                  disabled={busy}
                  className="h-9 w-9 rounded-full bg-white text-void flex items-center justify-center hover:bg-gradient-to-r hover:from-[#4f6bff] hover:to-[#9d6bff] hover:text-white transition-[background-color,color] duration-300 disabled:opacity-50"
                  data-testid="chatbot-send"
                  aria-label="Send"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
